import React, { useEffect, useState, useMemo } from 'react';
import '../styles/purchaseModal.css';
import Modal from '../../../shared/components/Modal';
import PurchaseActions from '../../../shared/components/PurchaseActions';

import { useAudio } from '../../../contexts/AudioContext';
import { normalizeShopImg } from '../utils/shopImages';
import { useTranslation } from 'react-i18next';
import { entityText } from '../../../i18n/entityText';
import { formatNumber } from '../../../i18n/format';
import { moduleTypeLabel } from '../../../shared/utils/gameLabels';

/**
 * Модалка покупки пака (PACK)
 *
 * Отличия от обычного PurchaseModal:
 * - Нет стрелок влево/вправо
 * - В центре не карточка товара, а список содержимого пака + блок иконок
 * - Всегда покупаем один конкретный пак
 */
export default function PackPurchaseModal({ pack, onClose }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const audioRef = useAudio();

  const accessToken = localStorage.getItem('accessToken');

  const priceUSDT = pack?.usdt ? parseFloat(pack.usdt) : null;
  // Цены в GRAM/Stars и подписи кнопок (сумма + иконка) считает общий PurchaseActions

  const packContent = pack?.packContent || {};

  // Список "галочек" (benefits)
  const benefits = useMemo(() => {
    const result = [];

    if (packContent.coins) {
      result.push({
        id: 'coins',
        label: t('shop.pack.coins', '{{n}} Coins', { n: formatNumber(packContent.coins) }),
      });
    }

    if (packContent.tickets) {
      result.push({
        id: 'tickets',
        label: t('shop.pack.tickets', {
          count: Number(packContent.tickets),
          defaultValue_one: '{{count}} Ticket',
          defaultValue_other: '{{count}} Tickets',
        }),
      });
    }

    if (Array.isArray(packContent.modules) && packContent.modules.length > 0) {
      packContent.modules.forEach((m, index) => {
        const qty = m.quantity || 1;
        let title = t('shop.pack.module', '{{n}} × Module', { n: qty });

        if (m.random) {
          title = t('shop.pack.random_module', '{{n}} × Random module', { n: qty });
        } else if (m.type) {
          title = t('shop.pack.typed_module', '{{n}} × Module: {{part}}', { n: qty, part: moduleTypeLabel(t, m.type) });
        }

        result.push({
          id: `module-${index}`,
          label: title,
        });
      });
    }

    if (Array.isArray(packContent.chests) && packContent.chests.length > 0) {
      packContent.chests.forEach((c, index) => {
        result.push({
          id: `chest-${index}`,
          label: t('shop.pack.chest', 'x{{n}} Chest', { n: c.quantity ?? 1 }),
        });
      });
    }

    if (Array.isArray(packContent.keys) && packContent.keys.length > 0) {
      packContent.keys.forEach((k, index) => {
        result.push({
          id: `key-${index}`,
          label: t('shop.pack.key', 'x{{n}} Key', { n: k.quantity ?? 1 }),
        });
      });
    }

    return result;
  }, [packContent, t]);

  // Контент для блока иконок (те же данные, что в карточке пака)
  const icons = useMemo(() => {
    const items = [];

    if (packContent.coins) {
      items.push({
        type: 'coins',
        amount: packContent.coins,
        icon: normalizeShopImg(packContent.coinsImg) || '/shop/coins/Coins_3.png',
      });
    }

    if (packContent.tickets) {
      items.push({
        type: 'tickets',
        amount: packContent.tickets,
        icon: normalizeShopImg(packContent.ticketsImg) || '/ticket_5.png',
        shineIcon: normalizeShopImg(packContent.ticketsShineImg) || '/shining_ticket.png',
      });
    }

    if (Array.isArray(packContent.modules) && packContent.modules.length > 0) {
      packContent.modules.forEach((module, index) => {
        items.push({
          type: 'module',
          amount: module.quantity || 1,
          icon: normalizeShopImg(module.img) || module.urlSmall || module.url || '/mystery.png',
        });
      });
    }

    if (Array.isArray(packContent.chests) && packContent.chests.length > 0) {
      packContent.chests.forEach((c, index) => {
        items.push({
          type: 'chest',
          amount: c.quantity ?? 1,
          icon: normalizeShopImg(c.img) || '/lootbox/icon_chest_green.png',
        });
      });
    }

    if (Array.isArray(packContent.keys) && packContent.keys.length > 0) {
      packContent.keys.forEach((k, index) => {
        items.push({
          type: 'key',
          amount: k.quantity ?? 1,
          icon: normalizeShopImg(k.img) || '/lootbox/icon_key_green.png',
        });
      });
    }

    return items;
  }, [packContent]);

  useEffect(() => {
    setVisible(true);
    // audioRef.current?.play('popup');
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
    // audioRef.current?.play('popup');
  };

  // Оплата вынесена в общий компонент PurchaseActions

  return (
    <Modal open={true} onClose={handleClose}>
      <Modal.Header title={entityText(t, 'shop_pack', pack?.id, 'name', pack?.name) || t('shop.pack.title', 'Pack')} onClose={handleClose} />
      <Modal.Body className="pack-purchase-content">
            {/* Список содержимого пака (галочки) */}
            <div className="pack-benefits-list">
              {benefits.map((benefit) => (
                <div key={benefit.id} className="pack-benefit-row">
                  <img
                    src="/stroke.png"
                    alt="check"
                    className="pack-benefit-icon"
                  />
                  <span className="pack-benefit-text">{benefit.label}</span>
                </div>
              ))}
            </div>

            {/* Блок иконок содержимого пака */}
            {icons.length > 0 && (
              <div className="pack-icons-block">
                {icons.map((icon, idx) => (
                  <div key={`${icon.type}-${idx}`} className="pack-icons-item">
                    <div className="pack-icons-image-wrapper">
                      {icon.shineIcon && (
                        <img
                          src={icon.shineIcon}
                          alt="shine"
                          className="pack-icons-shine"
                        />
                      )}
                      <img
                        src={icon.icon}
                        alt={icon.type}
                        className="pack-icons-image"
                      />
                    </div>
                    <span className="pack-icons-amount">
                      {icon.type === 'coins'
                        ? formatNumber(icon.amount)
                        : `x${icon.amount}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
      </Modal.Body>

      {/* Кнопки покупки - общий компонент */}
      <Modal.Footer>
        <PurchaseActions
          allowTON={!!(pack?.allowTon ?? pack?.allowTON)}
          allowStars={!!pack?.allowStars}
          purchaseType="PACK"
          itemId={pack?.id}
          usdPrice={priceUSDT}
          priceTON={pack?.priceTON}
          priceStars={pack?.priceStars}
          description={pack?.description
            ? entityText(t, 'shop_pack', pack.id, 'description', pack.description)
            : entityText(t, 'shop_pack', pack?.id, 'name', pack?.name)}
          disabled={!accessToken}
          itemLabel={entityText(t, 'shop_pack', pack?.id, 'name', pack?.name)}
          onClose={handleClose}
        />
      </Modal.Footer>
    </Modal>
  );
}


